import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Image as AntImage, Button } from 'antd';
import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import { FC, useCallback, useMemo, useState } from 'react';
import { UploadFile } from 'services/graphql/cms';
import BoatImagePlaceholder from '../../../../packages/sailor-assets/svg/no-image-placeholder.svg';
import styles from './BoatGallery.module.scss';
import { BoatGalleryProps } from './BoatGallery.props';

const BoatGallery: FC<BoatGalleryProps> = ({ files, image, boatName }) => {
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    mode: 'snap',
    slides: {
      perView: 'auto',
      spacing: 10,
    },
    initial: 0,
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    },
  });

  const hasCoverImage = useMemo(() => !!image?.url, [image?.url]);

  const handleOnItemImageClick = useCallback(
    (itemIndex) => {
      if (hasCoverImage) {
        setPreviewIndex(itemIndex + 1);
      } else {
        setPreviewIndex(itemIndex);
      }
      setShowPreview(true);
    },
    [hasCoverImage],
  );

  const handleCoverImageClick = useCallback(() => {
    setPreviewIndex(0);
    setShowPreview(true);
  }, []);

  const handlePreviewChange = useCallback((current: number) => {
    setPreviewIndex(current);
  }, []);

  const previewImages = useMemo(() => {
    let images: UploadFile[] = [];

    if (hasCoverImage) {
      images.push(image);
    }

    if (files?.length) {
      images = images.concat(files);
    }

    return images;
  }, [files, hasCoverImage, image]);

  return (
    <div className={styles.boatGalleryWrapper}>
      {image?.url ? (
        <div onClick={handleCoverImageClick} className={styles.coverWrapper}>
          <Image
            src={image.url}
            alt={image.alternativeText || boatName}
            width={1100}
            height={700}
            quality={100}
            priority
          />
        </div>
      ) : (
        <div className={styles.noCoverWrapper}>
          <BoatImagePlaceholder />
        </div>
      )}
      <div className={styles.galleryWrapper}>
        {files.length ? (
          <>
            {loaded && !!instanceRef.current && !showPreview && (
              <ul className={styles.actionsNav}>
                <li>
                  <Button
                    type="text"
                    className={styles.itemNavArrow}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      if (showPreview) {
                        setPreviewIndex((previousIndex) => (previousIndex > 0 ? previousIndex - 1 : previousIndex));
                      } else {
                        instanceRef.current?.prev();
                      }
                    }}
                    icon={<LeftOutlined className={styles.itemNavArrowIcon} />}
                  />
                </li>
                <li>
                  <Button
                    type="text"
                    className={styles.itemNavArrow}
                    icon={<RightOutlined className={styles.itemNavArrowIcon} />}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      if (showPreview) {
                        setPreviewIndex((previousIndex) =>
                          previousIndex < previewImages.length - 1 ? previousIndex + 1 : previousIndex,
                        );
                      } else {
                        instanceRef.current?.next();
                      }
                    }}
                  />
                </li>
              </ul>
            )}
            <div ref={ref} className="keen-slider">
              {files.map((item, index) => (
                <div
                  onClick={() => {
                    handleOnItemImageClick(index);
                  }}
                  key={item.documentId}
                  className={cn(styles.galleryImage, 'keen-slider__slide')}
                >
                  <Image src={item.url} alt={item.alternativeText || boatName} width={170} height={120} quality={100} />
                </div>
              ))}
            </div>
          </>
        ) : (
          <p>No gallery images</p>
        )}
        <div style={{ display: 'none' }}>
          <AntImage.PreviewGroup
            preview={{
              visible: showPreview,
              onVisibleChange: (isVisible) => setShowPreview(isVisible),
              current: previewIndex,
              onChange: handlePreviewChange,
            }}
          >
            {previewImages.map((item) => (
              <AntImage src={item.url} key={item.documentId} loading="lazy" alt={item.alternativeText || boatName} />
            ))}
          </AntImage.PreviewGroup>
        </div>
      </div>
    </div>
  );
};

export { BoatGallery };
